<script setup>
import moment from 'moment'
import {useUserGroupsStore, useUsersStore} from "@/stores"
import { numberWithCommas , simulation } from '@/plugins/misc'
import {
  emailValidator,
  requiredValidator,
} from '@validators'
import { ref, watch } from 'vue'
import { inRestrictedSimulation } from '@/utils/simulationScopes'

const props = defineProps({
  member: {
    type: Object,
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  userGroup: {
    type: Object
  },
  validInputs: {
    type: Boolean,
    default: true
  }
})
const emit = defineEmits(['update:modelValue', 'update:validInputs'])

// RM-337 item 5: no act-as button inside a limited act-as session (the server refuses it).
const restrictedSession = inRestrictedSimulation()
const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

//add userGroupsStore
const userGroupsStore = useUserGroupsStore()


const editing = ref(props.isEditing)
const isConfirmDeleteDialogOpen = ref(false)
const isEmailPreviewDialogOpen = ref(false)
const lowercaseEmail = computed({
  get() {
    return props.member.email
  },
  set(newValue) {
    props.member.email = newValue.toLowerCase()
  }
})

const notCurrentUserEmail = value => {
  return value !== myUser.value.email || "Email cannot be the same as the current user's email"
}

const emailValid = ref(true)


let savedUserGroup = null
const validateEmail = email => {
  const rules = [requiredValidator, emailValidator, notCurrentUserEmail]
  
  return rules.every(rule => rule(email) === true)
}


const startEditing = () => {
  editing.value = true
}

const cancelEditing = () => {
  userGroupsStore.revertAdminEditUserGroupMembers()
  editing.value = false
  if(props.userGroup.userPermissions[props.userGroup.userPermissions.length-1].isNew){
    props.userGroup.userPermissions.pop()
  }
  emit('update:validInputs', true)
}

const saveEditing = () => {
  userGroupsStore.saveAdminEditingUserGroupMembers()
  editing.value = false
}

const humanPermission = computed(() => {
  if(props.member.permission == 'regular'){
    return 'Platinum'
  }
  else if(props.member.permission == 'admin'){
    return 'Platinum + admin'
  }
  else if(props.member.permission == 'owner'){
    return 'Platinum + owner'
  }
  else if(props.member.permission == 'adminStandard'){
    return 'Admin only'
  }
  else if(props.member.permission == 'pending'){
    return 'Pending'
  }
  else{
    return 'Platinum'
  }
})

// computed comma separated string of emails of all members from props.userGroup.userPermissions that has receivesAllMemberApprovalsAndRequests == true
const allMembersToGetCcEmails = computed(() => {
  return props.userGroup.userPermissions.filter(member => member.receivesAllMemberApprovalsAndRequests).map(member => member.email).join(', ')
})

const emailSubject = computed(() => {
  if(props.member.isNew){
    if(props.member.permission == 'admin' || props.member.permission == 'adminStandard'){
      return `Added as an admin to ${props.userGroup.name}'s Platinum membership on RM`
    }
    else{
      return 'Platinum membership on RM from '+props.userGroup.name
    }
  }
  else{
    if(props.member.permission == 'admin' || props.member.permission == 'adminStandard'){
      return `Reminder: Added as an admin to ${props.userGroup.name}'s Platinum membership on RM`
    }
    else{
      return 'Reminder: Platinum membership on RM from '+props.userGroup.name
    }
  }

})

const lastActiveDisplay = date => {
  if (!date) return 'prior to 2/23/26'
  const days = moment().diff(moment(date), 'days')
  if (days < 7) return 'within the last week'
  if (days < 30) return 'within the last month'

  return moment(date).fromNow()
}

const permissionOptions = computed(() => {
  if(props.userGroup.type =='restaurant'){
    return [{text:'Platinum + admin', value: 'admin'},{text:'Platinum + owner', value: 'owner'}]
  } else{
    return [{text:'Platinum', value: 'regular'},{text:'Platinum + admin', value: 'admin'},{text:'Platinum + owner', value: 'owner'},{text:'Admin only (free)', value: 'adminStandard'}]
  }
})

watch(
  () => props.member.email,
  email => {
    emailValid.value = validateEmail(email)
    emit('update:validInputs', emailValid.value)

    // console.log("EMITTING",props.member.email,  emailValid.value)
  },
  { immediate: true }
)
</script>


<template>
  <div
    v-if="member"
  >
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDeleteDialogOpen"
      :confirmation-msg="(member.firstName ? 'Remove '+member.firstName+ ' '+member.lastName+'?' : 'Remove member?')"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
      @confirm="userGroup.userPermissions = userGroup.userPermissions.filter((item) => item.email !== member.email); saveEditing();"
    />
    <EmailPreviewDialog
      v-if="isEmailPreviewDialogOpen"
      v-model:is-dialog-visible="isEmailPreviewDialogOpen"
      :email-template-name="(member.permission == 'admin' || member.permission == 'adminStandard' ? 'enterpriseGroupInviteMemberAsAdmin' : 'enterpriseGroupInviteMember')"
      :email-to="member.email || `<new member email>`"
      :email-cc="`${myUser.email} ${(allMembersToGetCcEmails) ? ', '+allMembersToGetCcEmails : ''}, preferred@reserverm.com`"
      :email-subject="emailSubject"
      :member="member"
      :user-group="userGroup"
      @confirm="userGroup.userPermissions = userGroup.userPermissions.filter((item) => item.email !== member.email); saveEditing();"
    />
    <VCard class="pa-3 ma-2 ps-2">
      <div
        v-if="!editing && member.email != myUser.email && member.permission != 'pending' && (myUser?.userLevel == 'admin' || myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard')"
        class="float-right position-absolute"
        style="right: -2px; top: -2px;"
      >
        <VBtn
          color="grey-lighten-1"
          icon="mdi-edit"
          variant="text"
          @click="startEditing"
        />
        <VBtn
          color="grey-lighten-1"
          icon="mdi-close"
          variant="text"
          @click="isConfirmDeleteDialogOpen = true;"
        />
      </div>
      <div
        v-if="editing && member.email != myUser.email && member.permission != 'pending' && myUser?.userLevel == 'admin' && member?.emailConfirmed && !member.isNew && !restrictedSession"
        class="float-right position-absolute"
        style="right: -2px; top: -2px;"
      >
        <VBtn
          color="grey-lighten-1"
          icon="mdi-lightning-bolt"
          variant="text"
          @click="simulation(member.email)"
        />
      </div>
      <span class="text-h6">
        <div v-if="member?.firstName">{{ member.firstName }} {{ member.lastName }}</div>
        <div v-else-if="member?.isNew">New Member</div>
      </span>
      <span class="">
        <template
          v-if="member.isNew"
        >
          <VTextField
            v-model="lowercaseEmail"
            label="Email"
            autocomplete="null"
            :rules="[requiredValidator, emailValidator, notCurrentUserEmail]"
            class="email-input mt-1"
            style="width:100%"
          />
          <!--          <v-checkbox -->
          <!--            label="Make Admin?" -->
          <!--            v-model="member.permission" -->
          <!--            value="admin" -->
          <!--            false-value="regular"> -->

          <!--          </v-checkbox> -->
        </template>
        <template v-else-if="member?.email">{{ member.email }}</template>
      </span>
      <span>

        <template
          v-if="editing && (myUser.userLevel == 'admin' || myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard')"
          cols="auto"
        >
          <VSelect
            v-model="member.permission"
            density="compact"
            class="mb-4 mt-3"
            label="Member Type"
            item-title="text"
            item-value="value"
            :items="permissionOptions"
            required
          />
          <div
            v-if="member.permission == 'owner' || member.permission == 'admin' || member.permission == 'adminStandard'"
            cols="auto"
          >
            <VCheckbox
              v-model="member.receivesAllMemberApprovalsAndRequests"
              label="Receives new member request/invites?"
            />
          </div>

        </template>

        <template v-else-if="member.permission != 'regular'">
          <br><span>{{ humanPermission }}</span>
          <VCheckbox
            v-model="member.receivesAllMemberApprovalsAndRequests"
            label="Receives new member request/invites?"
            :disabled="true"
          />
        </template>

        <!--      <div class="text-caption mt-1" v-if="!member?.isNew && member?.emailConfirmed && (!member?.firstName || !member?.lastName || !member?.company || !member?.title || !member?.phoneNumber)">Incomplete member profile</div> -->
        <div
          v-if="!member?.isNew && !member?.emailConfirmed && !member?.sendInvite && !member?.dateInvited"
          class="text-caption mt-1"
        >
          Needs to activate their account (<span
            style="cursor: pointer; text-decoration: underline;"
            @click="member.sendInvite = true; member.invitedBy = myUser.email; saveEditing()"
          >Send Reminder</span>)
          <span
            class="cursor-pointer text-primary"
            @click="isEmailPreviewDialogOpen = true"
          >(view reminder)</span>
        </div>
        <div
          v-else-if="!member?.isNew && !member?.emailConfirmed && !member?.sendInvite && member?.dateInvited"
          class="text-caption mt-1"
        >
          <span v-if="userGroup.type =='restaurant'">Needs to register their account<br></span>
          <span v-else>Needs to register their account (<span
            style="cursor: pointer; text-decoration: underline;"
            @click="member.sendInvite = true; saveEditing()"
          >Send Reminder</span>) <span
            class="cursor-pointer text-primary"
            @click="isEmailPreviewDialogOpen = true"
          >(view reminder)</span><br></span>
          Member added {{ moment(member.dateInvited).format('MM/DD/YYYY') }}
        </div>
        <div
          v-else-if="member?.dateInvited && !(myUser.userLevel == 'admin' || myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard')"
          class="text-caption mt-1"
        >
          Last invite sent on {{ moment(member.dateInvited).format('MM/DD/YYYY') }}
        </div>
        <div
          v-if="member?.emailConfirmed"
          class="text-caption mt-1 text-medium-emphasis"
        >
          Last active: {{ lastActiveDisplay(member?.lastActiveAt) }}
        </div>
        <template v-if="member?.isNew">
          <div
            v-if="userGroup.type =='restaurant'"
            class="text-caption mt-1"
          >No invite will be sent on saving</div>
          <div
            v-else
            class="text-caption mt-1"
          >Member will receive an email invite with you cc'd<span
            class="cursor-pointer text-primary"
            @click="isEmailPreviewDialogOpen = true"
          >(view invite)</span></div>
        </template>
      </span>

      <span
        v-if="userGroup.type =='restaurant'"
        class="ma-0"
      >
        <VCol cols="auto">
          <label>Receives:</label>
          <!--        <v-checkbox -->
          <!--          v-model="member.receivesAdministrativeCorrespondence" -->
          <!--          :label="`Administrative Correspondence`" -->
          <!--        ></v-checkbox> -->
          <VCheckbox
            v-model="member.receivesGeneralRequests"
            label="General Dining Requests"
          />
          <VCheckbox
            v-model="member.receivesPrivateRequests"
            label="Private/Group Requests"
          />
        </VCol>
        <!--        <v-col cols="auto" > -->
        <!--          <span>Contact Preference?</span> -->
        <!--          <v-radio-group v-model="member.contactPreference" class="ml-4 mt-0" @change="" inline> -->
        <!--            <template v-slot:label> -->
        <!--            </template> -->
        <!--            <v-radio value="Email"> -->
        <!--              <template v-slot:label> -->
        <!--                <div>Email</div> -->
        <!--              </template> -->
        <!--            </v-radio> -->
        <!--            &lt;!&ndash;                      <v-radio value="Call">&ndash;&gt; -->
        <!--            &lt;!&ndash;                        <template v-slot:label>&ndash;&gt; -->
        <!--            &lt;!&ndash;                          <div>Call</div>&ndash;&gt; -->
        <!--            &lt;!&ndash;                        </template>&ndash;&gt; -->
        <!--            &lt;!&ndash;                      </v-radio>&ndash;&gt; -->
        <!--            <v-radio value="Text" -->
        <!--                     :disabled="true"> -->
        <!--              <template v-slot:label> -->
        <!--                <div>Text</div> -->
        <!--              </template> -->
        <!--            </v-radio> -->
        <!--          </v-radio-group> -->
        <!--        </v-col> -->
      </span>

      <span class="mt-1">
        <VCol
          v-if="member.permission == 'pending'"
          cols="auto"
        >
          <VBtn
            @click="member.permission = 'regular'; member.approve = true; saveEditing()"
          >Approve</VBtn>
          <VBtn
            class="ml-2"
            @click="userGroup.userPermissions = userGroup.userPermissions.filter((item) => item.email !== member.email); saveEditing()"
          >Deny</VBtn>
        </VCol>
        <VCol
          v-else-if="editing"
          cols="auto"
        >
          <VBtn
            :disabled="!member?.email || !emailValid || !validInputs"
            @click="saveEditing"
          >{{ member?.isNew ? 'Invite' : 'Save' }}</VBtn>
          <VBtn
            class="ml-2"
            @click="cancelEditing"
          >Cancel</VBtn>
        </VCol>

        <VSpacer />

      </span>
    </VCard>


    <!--    <v-divider class="ma-2" thickness="3"> </v-divider> -->
  </div>
</template>

<style lang="scss" scoped>
.admin-reservation-menu-button {
  position: relative;
  float: right;
  top: 0;
  right: 0;
}

.email-input{
  width: 17em;
}
.v-checkbox{
  margin: 0 !important;
  display: unset!important;
}
.v-card{
  line-height: 0.5em;
}
</style>
